'use client';

import React, { useState, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Shield, ArrowLeft, Send, CheckCircle2, Loader2 } from 'lucide-react';
import LandingNav from '@/app/public-landing-page/components/LandingNav';
import LandingFooter from '@/app/public-landing-page/components/LandingFooter';

const SUBJECTS = [
  'General',
  'Institution Partnership',
  'Technical Support',
  'Press & Media',
  'Investor Relations',
];

interface FormState {
  name: string;
  email: string;
  organisation: string;
  subject: string;
  message: string;
}

function ContactForm() {
  const searchParams = useSearchParams();
  const subjectParam = searchParams.get('subject')?.replace(/\+/g, ' ') || 'General';
  const fromParam = searchParams.get('from') || '';
  const fromInstitution = fromParam === 'institution-billing';
  const fromBilling = fromParam === 'billing';
  const backHref = fromInstitution
    ? '/institution-dashboard/billing'
    : fromBilling
      ? '/billing'
      : '/';
  const backLabel = fromInstitution || fromBilling ? 'Back to Billing' : 'Back to Ertzyx';

  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    organisation: '',
    subject: subjectParam,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          organisation: form.organisation.trim() || null,
          subject: form.subject,
          message: form.message.trim(),
          source_page: 'contact',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          (data as { error?: string }).error || 'Something went wrong. Please try again.'
        );
      }
      if ((data as { email_delivered?: boolean }).email_delivered === false) {
        console.warn('[contact] saved but email delivery failed');
      }

      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="public-page public-family-editorial">
      <LandingNav />
      <div className="border-b border-[var(--public-border)] bg-[var(--public-bg-subtle)] pt-20">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-6">
          <Link
            href={backHref}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} /> {backLabel}
          </Link>
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-[var(--public-sapphire)]" />
            <span className="font-bold text-slate-800">Ertzyx</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="public-heading mb-3 text-4xl">Contact Us</h1>
          <p className="text-slate-600">
            Have a question, partnership enquiry, or need support? Fill in the form below and we'll
            get back to you at{' '}
            <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
              support@ertzyx.com
            </a>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="public-card p-6">
              <h2 className="font-bold text-slate-800 mb-4">Get in Touch</h2>
              {[
                { label: 'Email', val: 'support@ertzyx.com\nyove@ertzyx.com' },
                { label: 'Platform', val: 'ledger.ertzyx.com' },
              ].map((i) => (
                <div key={i.label} className="mb-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">
                    {i.label}
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{i.val}</p>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-slate-50">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                  Response Times
                </p>
                {[
                  ['General enquiries', '2–3 business days'],
                  ['Technical support', '1–2 business days'],
                  ['Partnerships', '3–5 business days'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs py-1">
                    <span className="text-slate-600">{k}</span>
                    <span className="text-slate-400">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-50">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                  Legal
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    ['Privacy Policy', '/privacy-policy'],
                    ['Terms of Service', '/terms-of-service'],
                    ['Data Retention', '/data-retention'],
                    ['Cookie Policy', '/cookie-policy'],
                  ].map(([label, href]) => (
                    <Link key={label} href={href} className="text-xs text-blue-600 hover:underline">
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {success ? (
              <div className="public-card p-10 text-center">
                <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-800 mb-2">Message Sent</h3>
                <p data-clarity-mask="true" className="text-slate-600 mb-6">
                  Thank you for reaching out. We'll get back to you at <strong>{form.email}</strong>{' '}
                  within 2–3 business days.
                </p>
                <Link href={backHref}>
                  <button
                    className="px-6 py-3 rounded-xl text-sm font-bold text-white"
                    style={{ background: 'linear-gradient(135deg,#1B3A6B,#0d9488)' }}
                  >
                    {fromBilling ? 'Back to Billing' : 'Back to Ertzyx'}
                  </button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="public-card space-y-5 p-8">
                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                    <span>⚠</span> {error}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your full name"
                      className="w-full px-4 py-2.5 rounded-xl border border-teal-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-teal-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Organisation (optional)
                  </label>
                  <input
                    value={form.organisation}
                    onChange={(e) => setForm((f) => ({ ...f, organisation: e.target.value }))}
                    placeholder="Your company or institution"
                    className="w-full px-4 py-2.5 rounded-xl border border-teal-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-teal-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder="Tell us how we can help..."
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-xl border border-teal-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg,#1B3A6B,#0d9488)' }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Send Message
                    </>
                  )}
                </button>
                <p className="text-xs text-slate-400 text-center">
                  By submitting this form, you agree to our Privacy Policy. We will never share your
                  information with third parties.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
      <LandingFooter />
    </main>
  );
}

export default function ContactPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-teal-600" />
        </div>
      }
    >
      <ContactForm />
    </Suspense>
  );
}
